import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import useSEO from "@/hooks/useSEO";
import useLatestApk from "@/hooks/useLatestApk";
import { PageHeader } from "@/components/PageHeader";
import { DownloadCard } from "@/components/DownloadCard";
import useT from "@/i18n";
import { formatBytes, formatDate, releasedAt } from "@/lib/format";
import { breadcrumbLd, softwareLd, webPageLd } from "@/lib/seo";

const Row = ({ k, v, mono, testId }) => (
  <div className="grid grid-cols-1 xs:grid-cols-3 gap-1 xs:gap-4 py-3 border-b" style={{ borderColor: "rgba(255,255,255,.05)" }}>
    <dt className="text-xs tracking-[0.2em] uppercase text-slate-500">{k}</dt>
    <dd className={`xs:col-span-2 text-sm text-slate-200 break-all ${mono ? "font-mono-alt text-xs" : ""}`} data-testid={testId}>{v}</dd>
  </div>
);

export default function LatestVersion() {
  const { apk, loading } = useLatestApk();
  const { t: T, lp } = useT();
  const t = T.latest;
  const r = t.rows;
  const path = "/latest-version";
  const title = t.seoTitle(apk?.version_name);
  const description = t.seoDesc(apk, formatDate(releasedAt(apk), T.dateLocale));
  const crumbs = [{ name: t.crumb, path }];
  useSEO({ title, description, path, jsonLd: [webPageLd({ title, description, path: lp(path) }), breadcrumbLd(crumbs, T.crumbs.home, lp), softwareLd(apk, lp)] });

  return (
    <div data-testid="latest-version-page">
      <PageHeader crumbs={crumbs} badge={t.badge} title={loading ? t.titleLoading : apk ? t.title(apk.version_name) : t.titleNone} intro={t.intro} />
      <section className="max-w-7xl mx-auto safe-x sm:px-8 grid lg:grid-cols-12 gap-10 pb-16" aria-labelledby="details-heading">
        <article className="lg:col-span-5 glass rounded-3xl p-6 sm:p-8 min-w-0">
          <h2 id="details-heading" className="font-display text-2xl font-bold">{t.details}</h2>
          {apk ? (
            <dl className="mt-4">
              <Row k={r.version} v={`v${apk.version_name}`} testId="lv-version" />
              <Row k={r.code} v={`#${apk.version_code}`} testId="lv-code" />
              <Row k={r.released} v={formatDate(releasedAt(apk), T.dateLocale)} />
              <Row k={r.size} v={formatBytes(apk.size)} />
              <Row k={r.android} v={r.androidVal(apk.min_android)} />
              <Row k={r.force} v={apk.force_update ? r.forceYes : r.forceNo} testId="lv-force" />
              <Row k={r.sha} v={apk.sha256} mono testId="lv-sha256" />
            </dl>
          ) : (
            <p className="mt-4 text-slate-400 text-sm">{loading ? t.loading : t.none}</p>
          )}
          <Link to={lp("/changelog")} className="btn-ghost mt-6" data-testid="lv-changelog-link">{t.changelog} <ArrowRight size={16} aria-hidden="true" /></Link>
        </article>
        <div className="lg:col-span-7 min-w-0"><DownloadCard /></div>
      </section>
    </div>
  );
}
